"use client";

import { PlpFilterBar } from "@/components/ui/plp-filters";
import { SkeletonBar, SkeletonPulse } from "@/components/ui/skeleton";
import type { CatalogOrder } from "@/lib/host-panel/catalog-order";
import { PANEL_LAYER_CLASS } from "@/lib/host-panel/layer-stack";
import { cn } from "@/lib/utils";
import { CatalogOrderMenu } from "./catalog-order-menu";

/**
 * The sticky line under the filter block (handoff v2 §6.3): the counter on the left, the order
 * control on the right, one line — two on a phone when they do not fit. It sticks under the
 * site header while the grid scrolls.
 */
export function CatalogToolbar({
  counter,
  isCounterPending = false,
  order,
  orders,
  onOrderChange,
}: {
  /** "14 de 14 peças", or the served catalogue's page: "1 a 24 de 732 peças". */
  counter: string;
  /**
   * The slice the counter would name has not arrived: a skeleton takes the words' place. Never a
   * number read off a page that is not this slice's — the catalogue's own would read "732 peças"
   * under a category of twenty-two, and then correct itself.
   */
  isCounterPending?: boolean;
  order: CatalogOrder;
  orders: readonly { key: CatalogOrder; label: string }[];
  onOrderChange: (next: CatalogOrder) => void;
}) {
  return (
    <PlpFilterBar
      style={{ top: "var(--site-header-height, 75px)" }}
      className={cn(PANEL_LAYER_CLASS.facetRail, "top-[75px]")}
      railClassName="flex-wrap items-center gap-3.5 overflow-x-visible px-[clamp(20px,4vw,56px)] py-3"
    >
      <span aria-live="polite" className="flex-none font-mono text-[12px] tracking-[0.04em] tabular-nums text-[var(--fg-subtle)]">
        {isCounterPending ? (
          // The line's own height, so the bar under it does not move when the words arrive.
          <span aria-busy="true" data-testid="catalog-counter-skeleton" className="flex h-[18px] items-center">
            <SkeletonPulse>
              <SkeletonBar height={12} width={148} />
            </SkeletonPulse>
          </span>
        ) : (
          counter
        )}
      </span>
      <span aria-hidden="true" className="min-w-2 flex-auto" />
      {/* One option orders nothing (`ordemDisplay`): the control is not drawn. */}
      {orders.length > 1 ? (
        <span className="ml-auto flex flex-none items-center">
          <CatalogOrderMenu value={order} options={orders} onChange={onOrderChange} />
        </span>
      ) : null}
    </PlpFilterBar>
  );
}

/**
 * The counter's words (filters spec §6, prototype `contador`). A paged catalogue names the slice
 * it shows; a list in hand, how many of its pieces pass. Singular agrees.
 */
export function catalogCounter(
  input:
    | { kind: "paged"; page: number; pageSize: number; shown: number; total: number }
    | { kind: "list"; shown: number; total: number },
): string {
  if (input.kind === "list") return `${input.shown} de ${input.total} ${input.total === 1 ? "peça" : "peças"}`;

  const noun = input.total === 1 ? "peça" : "peças";
  if (input.total === 0) return `0 ${noun}`;

  const first = (input.page - 1) * input.pageSize + 1;
  const last = Math.min(first + input.shown - 1, input.total);

  return `${first} a ${last} de ${input.total.toLocaleString("pt-BR")} ${noun}`;
}
